import { compareOrderedNames, compareTopicsWithinArea } from './topicOrdering'

const normalizeOptionSource = value => (Array.isArray(value) ? value : [])

const removeOrderPrefix = value => (
  String(value || '').replace(/^\d+[_\s-]+/, '').trim()
)

const getDisplayName = item => (
  item?.beauty_name || removeOrderPrefix(item?.name)
)

// Units that describe an alternative reading of a series rather than its
// primary measurement, so they must never be the first option a user sees.
// Matching is by prefix because the dataset appends the period qualifier
// (e.g. "IMF definition, avg. of period", "IMF definition, end of period").
const DEPRIORITIZED_UNIT_PREFIXES = ['imf definition']

const isDeprioritizedUnit = unit => {
  const normalized = String(unit || '').trim().toLowerCase()
  return DEPRIORITIZED_UNIT_PREFIXES.some(prefix => normalized.startsWith(prefix))
}

// Keeps the incoming order but moves the deprioritized units to the end, so
// search cards and the viewer always default to the same primary unit.
export const sortUnitsForDisplay = units => {
  const source = normalizeOptionSource(units).filter(Boolean)
  return [
    ...source.filter(unit => !isDeprioritizedUnit(unit)),
    ...source.filter(isDeprioritizedUnit),
  ]
}

export const getUnitsInOrder = dataset => sortUnitsForDisplay(
  [...new Set(normalizeOptionSource(dataset).map(item => item.unit).filter(Boolean))]
)

export function buildOrderedIndicatorGroups(indicators, topics, areas, availableIds) {
  const indicatorSource = normalizeOptionSource(indicators)
  const topicSource = normalizeOptionSource(topics)
  const areaSource = normalizeOptionSource(areas)
  const filtered = availableIds
    ? indicatorSource.filter(item => availableIds.has(item.id))
    : indicatorSource

  return [...new Set(filtered.map(item => item.foreign_topic))]
    .map(topicId => {
      const topic = topicSource.find(item => item.id === topicId)
      const area = areaSource.find(item => item.id === topic?.foreign_area)
      const areaCode = area?.name || ''
      const topicCode = topic?.name || ''
      const areaLabel = getDisplayName(area) || 'Area'
      const topicLabel = getDisplayName(topic) || 'Topic'

      return {
        label: `${areaLabel} / ${topicLabel}`,
        areaCode,
        areaLabel,
        topicCode,
        topicLabel,
        options: filtered
          .filter(item => item.foreign_topic === topicId)
          .map(item => ({
            value: item.id,
            label: item.name,
            area: item.foreign_area,
            topic: item.foreign_topic,
            topicApi: topicCode,
            api: item.name,
          }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      }
    })
    .sort((a, b) => {
      const areaCompare = compareOrderedNames(
        a.areaCode,
        b.areaCode,
        a.areaLabel,
        b.areaLabel,
      )
      if (areaCompare !== 0) return areaCompare

      return compareTopicsWithinArea(
        a.areaLabel,
        a.topicLabel,
        b.topicLabel,
        a.topicCode,
        b.topicCode,
      )
    })
}
